import { useToggle } from '@/hooks/useToggle'
import { TaxoStage } from '@/kontent/taxonomies'
import { CustomSyllabusTab, TaxoStageWithLifeSkill } from '@/types'
import Grid from '@mui/material/Grid'
import {
	MouseEvent,
	ReactNode,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react'
import CustomModal, {
	CustomModalProps,
} from '../../legacy-ported/components/base/CustomModal'
import CustomSyllabusPicker, {
	BaseSyllabusPickerProps,
} from '../../legacy-ported/components/custom/CustomSyllabusPicker'

export interface ViewSelection {
	tabs: CustomSyllabusTab[]
	syllabuses?: string[]
	tags?: string[]
	stages?: TaxoStageWithLifeSkill[]
}

export interface CommonSyllabusModalProps
	extends BaseSyllabusPickerProps,
		Pick<
			CustomModalProps,
			'confirmButtonText' | 'hideConfirmButton' | 'hideCancelButton'
		> {
	/**
	 * Modal title
	 */
	title: string

	/**
	 * Show/Hide modal flag variable
	 */
	modalStatus: boolean

	/**
	 * Function to be used on the Cancel button
	 */
	onCancel?: (_event: MouseEvent<HTMLButtonElement>) => void

	/**
	 * Function to be used on the Confirm button
	 */
	onConfirm: (
		_selection: ViewSelection,
		_e?: MouseEvent<HTMLButtonElement>,
		_isPDF?: boolean,
	) => void

	/**
	 * Currently visible syllabus tabs
	 */
	selectedElements: CustomSyllabusTab[]

	/**
	 * Currently selected learning areas
	 */
	selectedSyllabuses?: string[]

	/**
	 * Currently selected stages
	 */
	selectedStages?: TaxoStageWithLifeSkill[]

	customModalSlotActions?: (_states: {
		showElementsError: boolean
		showLearningAreaError: boolean
		showStageError: boolean
		handleConfirm: (
			_e: MouseEvent<HTMLButtonElement>,
			_isPDF?: boolean,
		) => void
	}) => ReactNode

	isDownload?: boolean
	className?: string

	/**
	 * Custom text content for download modal dialog
	 */
	downloadModalText?: ReactNode

	/**
	 * Hide the learning areas column in the tree picker
	 */
	hideLearningAreasColumn?: boolean

	/**
	 * When true, hide the content-picker section entirely and skip its validation.
	 * Used by CEC syllabus downloads where users don't pick stages/elements —
	 * the modal only offers PDF/Word choice. Name is explicit ("CEC") to keep
	 * the standard-syllabus flow unambiguous.
	 */
	isCECHideContentPicker?: boolean
}

/**
 * Edit View Modal
 * @param props
 * @constructor
 */
export const CommonSyllabusModal = (props: CommonSyllabusModalProps) => {
	const {
		title,
		onConfirm,
		onCancel,
		modalStatus,
		selectedElements: initialSelectedElements,
		selectedSyllabuses: initialSelectedSyllabuses,
		selectedStages: initialSelectedStages,

		// additional
		syllabuses,
		keyLearningAreas,
		stages,
		disabledStages,
		disabledLearningAreas,
		customModalSlotActions,

		hideConfirmButton,
		hideCancelButton,
		confirmButtonText,
		syllabusElementsOptions,
		isDownload,
		className,
		downloadModalText,
		hideLearningAreasColumn,
		useNestedStageGroups,
		showElementsSelectAll,
		hideElementsExpandCollapse,
		showAllExpandables,
		customLifeSkillsLabel,
		hideLifeSkillsDivider,
		hideAllDividers,
		isCECHideContentPicker,
	} = props

	const refCustomSyllabusPicker = useRef<HTMLDivElement>()

	const [selectedElements, setElements] = useState<CustomSyllabusTab[]>(
		initialSelectedElements,
	)
	const [selectedSyllabuses, setSyllabuses] = useState(
		initialSelectedSyllabuses,
	)
	const [selectedStages, setStages] = useState<TaxoStageWithLifeSkill[]>(
		initialSelectedStages || [],
	)

	const [showElementsError, setShowElementsError] = useState(false)
	const [showLearningAreaError, setShowLearningAreaError] = useState(false)
	const [showStageError, setShowStageError] = useState(false)
	const [isDownloadIntroTruncate, toggleIsDownloadIntroTruncate] =
		useToggle(true)

	const [shouldShowToggleButton, setShouldShowToggleButton] = useState(false)

	// Default download modal text content
	const defaultDownloadModalText = useMemo(
		() => (
			<>
				When selecting multiple syllabuses to download, the syllabuses
				will appear in alphabetical order.
				<div style={{ marginTop: 16 }}>
					To optimise the presentation of your custom downloaded
					syllabus in Microsoft Word we recommend you save the
					document to your computer and click &lsquo;Enable
					Editing&rsquo; on PC or select the &lsquo;Editing&rsquo;
					option on Mac.
				</div>
			</>
		),
		[],
	)

	const textRef = useRef<HTMLDivElement>(null)

	// Detect Safari browser
	// Safari has known bugs with -webkit-line-clamp and inline elements (links)
	// Solution: Show plain text when truncated on Safari, RichText when expanded
	const isSafari = useMemo(() => {
		if (typeof window === 'undefined') return false
		return /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
	}, [])

	// Store the plain text version extracted from rendered content
	const [plainTextContent, setPlainTextContent] = useState<string>('')

	// Extract plain text from the actual rendered content for Safari
	useEffect(() => {
		if (isSafari && textRef.current) {
			// Get the text content from the actual rendered DOM
			const text =
				textRef.current.textContent || textRef.current.innerText || ''
			setPlainTextContent(text)
		}
	}, [isSafari, downloadModalText, defaultDownloadModalText])

	//measure rendered height
	useEffect(() => {
		const checkLines = () => {
			if (!textRef.current) return

			if (window.innerWidth <= 575) {
				// Only measure when text IS truncated
				// When expanded, we keep the button visible
				if (!isDownloadIntroTruncate) {
					return
				}

				// Use requestAnimationFrame to ensure layout is complete
				requestAnimationFrame(() => {
					if (!textRef.current) return

					// Simple and reliable check:
					// If scrollHeight > clientHeight, there's hidden overflow
					const hasOverflow =
						textRef.current.scrollHeight >
						textRef.current.clientHeight

					setShouldShowToggleButton(hasOverflow)
				})
			} else {
				setShouldShowToggleButton(false)
			}
		}

		// Only check when modal is open
		if (!modalStatus) {
			setShouldShowToggleButton(false)
			return
		}

		// Wait for modal animation and content to render
		// Use longer timeout on mobile for RichText to fully render
		const timer = setTimeout(
			() => {
				checkLines()
			},
			window.innerWidth <= 575 ? 500 : 300,
		)

		window.addEventListener('resize', checkLines)

		return () => {
			clearTimeout(timer)
			window.removeEventListener('resize', checkLines)
		}
	}, [
		downloadModalText,
		defaultDownloadModalText,
		modalStatus,
		isDownloadIntroTruncate,
		isSafari,
	])

	const handleConfirm = (
		_e: MouseEvent<HTMLButtonElement>,
		isPDF = false,
	) => {
		// CEC downloads have no picker — no stage / element / learning-area to
		// validate. Short-circuit before any validation runs so the normal flow's
		// tabError / stageError / laError rules can never block a CEC download.
		if (isCECHideContentPicker) {
			onConfirm(
				{
					tabs: [],
					syllabuses: initialSelectedSyllabuses ?? [],
					stages: [],
				},
				_e,
				isPDF,
			)
			return
		}

		let tabError = false
		let laError = false
		let tagError = false
		let stageError = false

		tabError = selectedElements.length === 0
		if (hideLearningAreasColumn) {
			const shortElements =
				selectedElements && selectedElements.length > 0
					? selectedElements.filter((e) =>
							shortElementsIds.includes(e),
					  )
					: []
			tabError = shortElements.length === 0
		}
		setShowElementsError(tabError)

		if (selectedSyllabuses) {
			laError = selectedSyllabuses.length === 0
			setShowLearningAreaError(laError)
		}

		if (selectedStages) {
			stageError = selectedStages.length === 0
			setShowStageError(stageError)
		}

		if (!tabError && !laError && !tagError && !stageError) {
			onConfirm(
				{
					tabs: selectedElements,
					syllabuses: selectedSyllabuses,
					stages: selectedStages,
				},
				_e,
				isPDF,
			)
		} else {
			// Scroll to error
			setTimeout(() => {
				refCustomSyllabusPicker.current
					.querySelector('.nsw-in-page-alert')
					?.parentElement.parentElement.scrollIntoView()
			}, 0)
		}
	}

	const handleCancel = (e: MouseEvent<HTMLButtonElement>) => {
		setShowElementsError(false)
		setShowLearningAreaError(false)
		setShowStageError(false)

		if (onCancel) {
			onCancel(e)
		}
	}

	const shortElementsIds = [
		'course-overview',
		'rationale',
		'aim',
		'outcomes',
		'content',
		'access-points',
	]

	const handleChangeSyllabus = (selected: {
		learningAreas: string[]
		stages: TaxoStage[]
		elements: CustomSyllabusTab[]
	}) => {
		setSyllabuses(selected.learningAreas)
		setStages(selected.stages as TaxoStageWithLifeSkill[])
		setElements(selected.elements)

		setShowLearningAreaError(!selected.learningAreas.length)
		setShowStageError(!selected.stages.length)

		let showModalElementsError = !selected.elements.length
		if (hideLearningAreasColumn) {
			const shortElements =
				selected.elements && selected.elements.length > 0
					? selected.elements.filter((e) =>
							shortElementsIds.includes(e),
					  )
					: []
			showModalElementsError = !shortElements.length
		}

		setShowElementsError(showModalElementsError)
	}

	return (
		<CustomModal
			className={className}
			css={{
				'& .MuiDialog-paper': {
					width: '100%',
					maxWidth: 1029,
				},
				...(isCECHideContentPicker && {
					'& .MuiDialogContent-root': { display: 'none' },
					'& .MuiDialogActions-root': { borderTop: 'none' },
				}),
			}}
			title={title}
			modalStatus={modalStatus}
			handleConfirm={onConfirm ? handleConfirm : undefined}
			handleCancel={handleCancel}
			maxWidth="lg"
			slotActions={
				customModalSlotActions
					? customModalSlotActions({
							showElementsError,
							showLearningAreaError,
							showStageError,
							handleConfirm,
					  })
					: undefined
			}
			slotAfterTitle={
				isDownload && (
					<>
						<div
							ref={textRef}
							className="text-base font-normal"
							style={{ marginTop: 24 }}
							css={
								isDownloadIntroTruncate
									? {
											'@media (max-width: 575px)': {
												display: '-webkit-box',
												WebkitBoxOrient: 'vertical',
												WebkitLineClamp: 3,
												overflow: 'hidden',
												textOverflow: 'ellipsis',
												lineHeight: 1.4,
												whiteSpace: 'normal',
												maxHeight: '4.2em', // Fallback (3 lines × 1.4 line-height)
												overflowWrap: 'break-word',
											},
									  }
									: undefined
							}
						>
							{isSafari &&
							isDownloadIntroTruncate &&
							plainTextContent
								? plainTextContent
								: downloadModalText || defaultDownloadModalText}
						</div>
						<div
							className="flex sm:hidden justify-end"
							css={{
								opacity: shouldShowToggleButton ? 1 : 0,
								transition: 'opacity 0.3s ease-in',
								pointerEvents: shouldShowToggleButton
									? 'auto'
									: 'none',
							}}
						>
							<button
								type="button"
								onClick={toggleIsDownloadIntroTruncate}
								className="text-base underline text-nsw-brand-dark mt-2"
							>
								{isDownloadIntroTruncate
									? 'Show more'
									: 'Show less'}
							</button>
						</div>
					</>
				)
			}
			hideConfirmButton={hideConfirmButton}
			hideCancelButton={hideCancelButton}
			confirmButtonText={confirmButtonText}
		>
			{!isCECHideContentPicker && (
				<Grid className="syllabus-header-dialog">
					<div className="syllabus-header-dialog__tree-picker-wrapper">
						<CustomSyllabusPicker
							ref={refCustomSyllabusPicker}
							syllabuses={syllabuses}
							keyLearningAreas={keyLearningAreas}
							stages={stages}
							syllabusElementsOptions={syllabusElementsOptions}
							disabledLearningAreas={disabledLearningAreas}
							disabledStages={disabledStages}
							showLearningAreaError={showLearningAreaError}
							showStageError={showStageError}
							showElementsError={showElementsError}
							onChange={handleChangeSyllabus}
							initialSelectedSyllabuses={
								initialSelectedSyllabuses
							}
							initialSelectedStages={initialSelectedStages}
							initialSelectedElements={initialSelectedElements}
							hideLearningAreasColumn={hideLearningAreasColumn}
							useNestedStageGroups={useNestedStageGroups}
							showElementsSelectAll={showElementsSelectAll}
							hideElementsExpandCollapse={
								hideElementsExpandCollapse
							}
							showAllExpandables={showAllExpandables}
							customLifeSkillsLabel={customLifeSkillsLabel}
							hideLifeSkillsDivider={hideLifeSkillsDivider}
							hideAllDividers={hideAllDividers}
							disableAutoContentSelection={false}
						/>
					</div>
				</Grid>
			)}
		</CustomModal>
	)
}
